// The owner of an organisation, as head office sees it (spec/14 §3.1; spec/07 §6.5): who to invite —
// by email or by phone, exactly one, and by email only while phone sign-in is off on the Platform
// (`auth.phoneSignIn`, since 2026-10-08) — and what became of an invitation. Used by creating an
// organisation, which invites its owner in the same step, and by the organisation's page, which
// invites one again while the organisation has none.

import type { CreatedInvitation, OwnerInvitationContact } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { RadioGroup, TextField } from '@clavian/ui-web';
import { useEffect, type ReactNode } from 'react';
import { Controller, useController, type Control, type FieldValues, type Path } from 'react-hook-form';

import { fieldErrors } from '../../api/problems';
import { usePhoneSignIn } from '../../auth/sign-in-methods';

export type OwnerChannel = 'email' | 'phone';

export interface OwnerContactValues {
  readonly channel: OwnerChannel;
  readonly email: string;
  readonly phoneNumber: string;
}

export const EMPTY_OWNER: OwnerContactValues = { channel: 'email', email: '', phoneNumber: '' };

/** The contract's `PhoneNumber`: E.164 (spec/07 §2.1). */
const E164 = /^\+[1-9]\d{1,14}$/;
/** The contract's check on an owner's address. The server checks it again (`400`). */
const EMAIL = /^[^\s@]+@[^\s@]+$/;

/** What a person types, as E.164: spaces, brackets and hyphens are how numbers are written, not part of them. */
export const compactPhone = (phone: string): string => phone.replace(/[\s()-]+/g, '');

/** The body the api takes: the one contact the channel names. */
export function ownerContactOf(values: OwnerContactValues): OwnerInvitationContact {
  return values.channel === 'email' ? { email: values.email.trim() } : { phoneNumber: compactPhone(values.phoneNumber) };
}

/**
 * The `400`'s field errors placed on the fields they name — `names` maps the api's path to the form's
 * — and the messages of any the form has no field for, to show above it.
 */
export function placeFieldErrors(
  error: unknown,
  names: Readonly<Record<string, string>>,
  place: (name: string, message: string) => void,
): readonly string[] {
  const unplaced: string[] = [];
  for (const problem of fieldErrors(error)) {
    const name = names[problem.field];
    if (name === undefined) unplaced.push(problem.message);
    else place(name, problem.message);
  }
  return unplaced;
}

/** Email or phone, and the one field for it. `names` are the form's paths for the three values. */
export function OwnerContactFields<TValues extends FieldValues>({
  control,
  names,
  channel,
}: {
  readonly control: Control<TValues>;
  readonly names: { readonly channel: Path<TValues>; readonly email: Path<TValues>; readonly phoneNumber: Path<TValues> };
  readonly channel: OwnerChannel;
}): ReactNode {
  const { t } = useTranslation('hq');
  // While phone sign-in is off there is no choice to make: email. A form left on phone goes back to email.
  const phoneOn = usePhoneSignIn();
  const { field: channelField } = useController({ control, name: names.channel });
  const setChannel = channelField.onChange;
  useEffect(() => {
    if (!phoneOn && channel === 'phone') setChannel('email');
  }, [phoneOn, channel, setChannel]);
  return (
    <>
      {phoneOn ? (
        <Controller
          control={control}
          name={names.channel}
          render={({ field }) => (
            <RadioGroup
              label={t('organizations.new.field.ownerBy')}
              orientation="horizontal"
              name={field.name}
              value={field.value as string}
              onValueChange={field.onChange}
              options={[
                { value: 'email', label: t('organizations.new.channel.email') },
                { value: 'phone', label: t('organizations.new.channel.phone') },
              ]}
            />
          )}
        />
      ) : null}
      {channel === 'email' || !phoneOn ? (
        <Controller
          key="email"
          control={control}
          name={names.email}
          rules={{
            validate: (value: unknown) => {
              const text = String(value ?? '').trim();
              if (text === '') return t('organizations.new.required');
              return EMAIL.test(text) || t('organizations.new.invalidEmail');
            },
          }}
          render={({ field, fieldState }) => (
            <TextField
              className="console-hq-field"
              label={t('organizations.new.field.email')}
              type="email"
              autoComplete="off"
              spellCheck={false}
              required
              name={field.name}
              value={field.value as string}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={fieldState.error?.message}
            />
          )}
        />
      ) : (
        <Controller
          key="phone"
          control={control}
          name={names.phoneNumber}
          rules={{
            validate: (value: unknown) => {
              const text = compactPhone(String(value ?? ''));
              if (text === '') return t('organizations.new.required');
              return E164.test(text) || t('organizations.new.invalidPhone');
            },
          }}
          render={({ field, fieldState }) => (
            <TextField
              className="console-hq-field"
              label={t('organizations.new.field.phoneNumber')}
              description={t('organizations.new.hint.phoneNumber')}
              type="tel"
              autoComplete="off"
              required
              name={field.name}
              value={field.value as string}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={fieldState.error?.message}
            />
          )}
        />
      )}
    </>
  );
}

/**
 * An invitation just made: to whom (masked, as the api returns it), until when, and whether its
 * message went. When it did not — a phone invitation waits for SMS and WhatsApp (F-CMP-09) — the link
 * is shown for head office to pass on. The api hands it over once, with the invitation.
 */
export function InvitationOutcome({ invitation }: { readonly invitation: CreatedInvitation }): ReactNode {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  const expires = format.dateTime(invitation.expiresAt, { date: 'medium', time: 'short' });
  const sent = invitation.delivery.status === 'sent';
  const channel = invitation.delivery.channel ?? (invitation.channel === 'email' ? 'email' : 'sms');
  return (
    <div className="console-notice console-hq-invitation" role="status">
      <p>{t('organizations.owner.invited', { destination: invitation.destination, expires })}</p>
      {sent ? (
        <p>{t('organizations.owner.sent', { channel: t(`organizations.owner.channel.${channel}`) })}</p>
      ) : (
        <>
          <p>{t('organizations.owner.notSent')}</p>
          <p>
            <span className="console-muted">{t('organizations.owner.link')}</span>{' '}
            <span className="console-code">{invitation.acceptUrl}</span>
          </p>
        </>
      )}
    </div>
  );
}
